import { useMemo, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { DateInput } from '@/components/ui/date-input';
import { AlertTriangle } from 'lucide-react';

function parseFilenameFromContentDisposition(header: string | null): string | null {
  if (!header) return null;
  const m = /filename\*?=(?:UTF-8''|")?([^\";]+)"?/i.exec(header);
  if (!m?.[1]) return null;
  try {
    return decodeURIComponent(m[1]);
  } catch {
    return m[1];
  }
}

function triggerBrowserDownload(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.style.display = 'none';
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 2000);
}

export function GeneralAffiliateReportsPage() {
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [reportLoading, setReportLoading] = useState(false);
  const [reportError, setReportError] = useState<string | null>(null);
  const canGenerate = useMemo(() => !!startDate && !!endDate, [startDate, endDate]);

  async function downloadGeneralAffiliateReportXlsx(params: { startDate: string; endDate: string }) {
    const resp = await fetch('/v1/api/affiliate-reporter/export/', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ start_date: params.startDate, end_date: params.endDate }),
    });

    if (!resp.ok) {
      const text = await resp.text().catch(() => '');
      throw new Error(`Request failed (${resp.status})${text ? `: ${text}` : ''}`);
    }

    const contentDisposition = resp.headers.get('content-disposition');
    const fallbackName = `affiliate_report_${params.startDate}_to_${params.endDate}.xlsx`;
    const filename = parseFilenameFromContentDisposition(contentDisposition) ?? fallbackName;
    const blob = await resp.blob();
    triggerBrowserDownload(blob, filename);
  }

  async function handleGenerate() {
    if (!canGenerate || reportLoading) return;
    setReportLoading(true);
    setReportError(null);
    try {
      await downloadGeneralAffiliateReportXlsx({ startDate, endDate });
    } catch (e) {
      setReportError(e instanceof Error ? e.message : 'Failed to generate report.');
    } finally {
      setReportLoading(false);
    }
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Generate General Affiliate Report</CardTitle>
          <CardDescription>
            Select date range to generate the general affiliate report.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 grid-cols-1 md:grid-cols-2">
            <DateInput label="Start Date" value={startDate} onChange={(e) => setStartDate(e.currentTarget.value)} />
            <DateInput label="End Date" value={endDate} onChange={(e) => setEndDate(e.currentTarget.value)} />
          </div>
          <Button onClick={handleGenerate} disabled={!canGenerate || reportLoading} className="mt-2">
            {reportLoading ? 'Generating...' : 'Create Report'}
          </Button>
          {reportError && (
            <div className="flex items-start gap-2 text-sm text-red-700 dark:text-red-400">
              <AlertTriangle className="h-4 w-4 mt-0.5" />
              <span className="break-words">{reportError}</span>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
